import { ScraperFilters } from "@features/cx/shared/utils/filters";
import { DropdownItem } from "@shared/api/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useServiceCategory, useServiceStatus } from "../../hooks";

interface FilterPanelProps {
  currentFilters?: ScraperFilters;
  onApplyFilters: (filters: Partial<ScraperFilters>) => void;
  onResetFilters: () => void;
  tab: number;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters = {},
  onApplyFilters,
  onResetFilters,
  tab,
}) => {
  const remoteUtilityId = useRemoteUtilityId();

  // Fetch categories and statuses
  const { data: categoriesResponse } = useServiceCategory({
    remoteUtilityId: Number(remoteUtilityId),
  });
  const { data: statusesResponse } = useServiceStatus({
    remoteUtilityId: Number(remoteUtilityId),
    tab,
  });

  // Extract the result arrays from the API responses
  const categories = categoriesResponse || [];
  const statuses = statusesResponse?.result || [];

  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    status: number[];
    category: string;
    name: string;
  }>({
    status: currentFilters?.status || [],
    category: currentFilters?.category || "ALL_CATEGORIES",
    name: currentFilters?.name || "ALL_CATEGORIES",
  });

  // Convert numeric status to string for form
  const selectedStatusValue =
    tempFilters.status.length > 0
      ? String(tempFilters.status[0])
      : "ALL_STATUS";

  // Initialize form with current filter values
  const form = useForm({
    defaultValues: {
      status: selectedStatusValue,
      category: tempFilters.category || "ALL_CATEGORIES",
      name: tempFilters.name || "ALL_CATEGORIES",
    },
  });

  // Update temp filters when current filters change (e.g., from URL)
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters?.status || [],
      category: currentFilters?.category || "ALL_CATEGORIES",
      name: currentFilters?.name || "ALL_CATEGORIES",
    };

    setTempFilters(newTempFilters);

    // Update form values
    const newSelectedStatusValue =
      newTempFilters.status.length > 0
        ? String(newTempFilters.status[0])
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("category", newTempFilters.category);
    form.setValue("name", newTempFilters.name);
  }, [currentFilters, form]);

  // Update form when tempFilters change
  useEffect(() => {
    const newSelectedStatusValue =
      tempFilters.status.length > 0
        ? String(tempFilters.status[0])
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("category", tempFilters.category);
    form.setValue("name", tempFilters.name);
  }, [tempFilters, form]);

  const handleStatusChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      status: value === "ALL_STATUS" ? [] : [Number(value)],
    }));
  };

  const handleCategoryChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      category: value === "ALL_CATEGORIES" ? "" : value,
    }));
  };

  const handleNameChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      name: value === "ALL_CATEGORIES" ? "" : value,
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply: Partial<ScraperFilters> = {};

    if (tempFilters.status.length > 0) {
      filtersToApply.status = tempFilters.status;
    }
    if (tempFilters.name && tempFilters.name !== "ALL_CATEGORIES") {
      filtersToApply.name = tempFilters.name;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: [],
      name: "ALL_CATEGORIES",
      category: "ALL_CATEGORIES",
    });
    onResetFilters();
  };

  const handleSubmit = (data: any) => {
    handleApplyFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Account Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select
                value={selectedStatusValue}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Status</SelectItem>
                    {statuses.map((status: DropdownItem) => (
                      <SelectItem key={status.key} value={String(status.key)}>
                        {status.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Account Category</Label>
          <Controller
            name="name"
            control={form.control}
            render={({ field }) => (
              <Select
                value={tempFilters.name || "ALL_CATEGORIES"}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleNameChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_CATEGORIES">
                      All Categories
                    </SelectItem>
                    {categories.map((category: any) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
